import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import UTRModal from '../../../Modal';
import {
    fetchSavedListNotificationPreferences,
    updateSavedListNotificationPreferences,
} from '../../../../actions/saved-lists-actions';

import styles from './savedListNotificationsModal.scss';

const TESTID_PREFIX = 'modal.saved-list-notifications';

// Modal chrome header (UTRModal's mobile header title); the shared shell renders it in Title Case.
const MODAL_HEADER_TITLE = 'Notification Settings';
const MODAL_TITLE = 'Saved List Notifications';
const MODAL_SUBTITLE = 'Get a weekly email when there is a new activity across your saved player lists.';
const ACTIVITY_HEADING = 'Activity includes:';
const ACTIVITY_ITEMS = [
    { key: 'rating-changes', title: 'Rating Changes', description: 'When a player\'s UTR rating changes by 0.1 or more.' },
    { key: 'new-match-results', title: 'New Match Results', description: 'When a player has a new match result.' },
    { key: 'event-registrations', title: 'Event Registrations', description: 'When a player registers for an event.' },
];
const CANCEL_LABEL = 'Cancel';
const TURN_ON_LABEL = 'Turn On';
const TURN_OFF_LABEL = 'Turn Off';

const SavedListNotificationsModal = ({ onClose }) => {
    const dispatch = useDispatch();
    const notificationsEnabled = useSelector((state) => state.savedLists.notificationsEnabled);
    const isLoadingPreferences = useSelector((state) => state.savedLists.isLoadingNotificationPreferences);
    const preferencesLoaded = useSelector((state) => state.savedLists.notificationPreferencesLoaded);
    const isUpdatingPreferences = useSelector((state) => state.savedLists.isUpdatingNotificationPreferences);

    useEffect(() => {
        dispatch(fetchSavedListNotificationPreferences());
    }, [dispatch]);

    // The button stays disabled while the initial preferences fetch is in flight so an early click
    // can't PUT a toggle computed from the not-yet-loaded (default) value — but only the toggle update
    // itself shows the spinner. The opening fetch is a silent load, not a busy button.
    const isFetching = isLoadingPreferences || !preferencesLoaded;
    const isCtaDisabled = isFetching || isUpdatingPreferences;
    const ctaLabel = notificationsEnabled ? TURN_OFF_LABEL : TURN_ON_LABEL;

    const handleToggle = () => {
        dispatch(updateSavedListNotificationPreferences(!notificationsEnabled)).then(onClose);
    };

    return (
        <UTRModal
            title={MODAL_HEADER_TITLE}
            onCloseClick={onClose}
            className={styles.notificationsModal}
            data-testid={TESTID_PREFIX}
        >
            <div className={styles.content} data-testid={`${TESTID_PREFIX}.content`}>
                <div className={styles.title} data-testid={`${TESTID_PREFIX}.body-title`}>{MODAL_TITLE}</div>
                <p className={styles.subtitle} data-testid={`${TESTID_PREFIX}.subtitle`}>{MODAL_SUBTITLE}</p>
                <hr className={styles.divider} />
                <p className={styles.activityHeading} data-testid={`${TESTID_PREFIX}.activity-heading`}>{ACTIVITY_HEADING}</p>
                <ul className={styles.activityList} data-testid={`${TESTID_PREFIX}.activity-list`}>
                    {ACTIVITY_ITEMS.map((item) => (
                        <li
                            key={item.key}
                            className={styles.activityItem}
                            data-testid={`${TESTID_PREFIX}.activity-list.item-${item.key}`}
                        >
                            <span
                                className={styles.activityTitle}
                                data-testid={`${TESTID_PREFIX}.activity-list.item-${item.key}.title`}
                            >
                                {item.title}
                            </span>
                            <span
                                className={styles.activityDescription}
                                data-testid={`${TESTID_PREFIX}.activity-list.item-${item.key}.description`}
                            >
                                {item.description}
                            </span>
                        </li>
                    ))}
                </ul>
                <div className={styles.actions} data-testid={`${TESTID_PREFIX}.actions`}>
                    <button
                        type="button"
                        className={`btn ${styles.cancelBtn}`}
                        onClick={onClose}
                        data-testid={`${TESTID_PREFIX}.cancel-btn`}
                    >
                        {CANCEL_LABEL}
                    </button>
                    <button
                        type="button"
                        className={`btn ${styles.ctaBtn}`}
                        onClick={handleToggle}
                        disabled={isCtaDisabled}
                        data-testid={`${TESTID_PREFIX}.toggle-btn`}
                    >
                        {isUpdatingPreferences && (
                            <i
                                className={`fas fa-circle-notch fa-spin ${styles.ctaSpinner}`}
                                data-testid={`${TESTID_PREFIX}.toggle-btn.loading`}
                            />
                        )}
                        {ctaLabel}
                    </button>
                </div>
            </div>
        </UTRModal>
    );
};

SavedListNotificationsModal.propTypes = {
    onClose: PropTypes.func.isRequired,
};

export default SavedListNotificationsModal;
